import {
  Body,
  Icon as DSIcon,
  type IconType,
} from '@assembly-js/design-system';
import React from 'react';

/**
 * How to draw the app's mark. Builder apps carry a design-system icon key, while
 * marketplace apps carry a hosted image, so the card takes whichever the surface
 * has rather than forcing both through one shape.
 */
export type AppLimitAppIcon =
  | { source: 'icon'; name: IconType }
  | { source: 'image'; url: string };

/** The app the paywall is holding back, as the modal renders it. */
export interface AppLimitApp {
  name: string;
  icon: AppLimitAppIcon;
  /** The app's own blurb, under its name. Absent on rows that never got one. */
  description?: string;
}

const AppMark: React.FC<{ icon: AppLimitAppIcon }> = ({ icon }) =>
  icon.source === 'image' ? (
    <img src={icon.url} alt="" className="size-5 shrink-0 object-contain" />
  ) : (
    <DSIcon icon={icon.name} className="size-5 shrink-0 text-black-heading" />
  );

/**
 * The app the user is one click away from, named inside the app-limit paywall.
 *
 * Naming it is the whole point of the card — the paywall interrupts a build the
 * user has already shaped, so it reads as "this app, for this fee" rather than a
 * generic upsell.
 */
export const AppLimitAppCard: React.FC<{ app: AppLimitApp }> = ({ app }) => (
  // The design fixes the card at 92px — two lines of blurb — and centres the
  // block inside it, so a one-line app doesn't sit shorter than a two-line one.
  // box-border because Tailwind's preflight is off here: without it the padding
  // and border stack on top of the 92px and the card comes out 18px too tall.
  <div className="box-border flex min-h-[92px] items-center rounded-lg border border-solid border-gray-200 bg-gray-100 px-3 py-2">
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <AppMark icon={app.icon} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Body
          tag="div"
          className="truncate font-medium leading-[22px] text-black-heading"
        >
          {app.name}
        </Body>
        {app.description && (
          <Body
            tag="div"
            size="xs"
            className="line-clamp-2 leading-[20px] text-gray-small"
          >
            {app.description}
          </Body>
        )}
      </div>
    </div>
  </div>
);
